import { Injectable } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { Pagination, HttpPagination } from '@library/common/http';

import { timer, Observable, Subject, of, OperatorFunction, pipe, from, throwError } from 'rxjs';
import { map, tap, take, shareReplay, switchMap, finalize, filter, takeUntil, mergeMap, catchError } from 'rxjs/operators';

export const DefaultExpiryLength = 5; // Minutes
export const ErrorCacheDuration = 30; // Seconds - prevents retrying failed requests

class RequestOptions<T, R> {
   url: string;
   params?: any;
   headers?: any;
   pipe?: OperatorFunction<T, R>;
   length?: number = DefaultExpiryLength;
   collection?: R;
   expiry?: number;
   refresher?: Subject<void>;
   cache?: string;
   pagination?: Pagination;
   useLambda?: boolean;
   useLocalLambda?: boolean;
   noCache?: boolean = false;
   cancelled?: boolean = false;
   // Track fetched pages for resumable pagination
   fetchedPages?: Set<number>;
   totalPages?: number;

   constructor( opts: RequestOptions<any, any> ) {
      this.url = opts?.url;
      if ( opts?.params ) { this.params = opts.params; }

      this.pipe = opts?.pipe ?? pipe();
      this.length = opts?.length ?? DefaultExpiryLength;
      if ( opts?.collection ) { this.collection = opts.collection; }
      if ( opts?.expiry ) { this.expiry = opts.expiry; }
      if ( opts?.refresher ) { this.refresher = opts.refresher; }
      this.cache = opts.url + ( opts?.params ? JSON.stringify( opts.params ) : '' );
      if ( opts?.pagination ) { this.pagination = opts.pagination; }
      if ( opts?.headers ) { this.headers = opts.headers; }
      if ( opts?.useLambda ) { this.useLambda = opts.useLambda; }
      if ( opts?.useLocalLambda ) { this.useLocalLambda = opts.useLocalLambda; }
      if ( opts?.cancelled ) { this.cancelled = typeof opts.cancelled === 'boolean' && opts.cancelled; }
      // Preserve fetched pages tracking for resumption
      if ( opts?.fetchedPages ) { this.fetchedPages = opts.fetchedPages; }
      if ( opts?.totalPages ) { this.totalPages = opts.totalPages; }
   }
}

@Injectable({
   providedIn: 'root'
})
export class CacheService {

   private static PARALLEL_REQUESTS_LIMIT = 6;

   cached = {};
   expiry = {};
   noCache: Map<string, boolean> = new Map();

   subscriptions: Map<string, RequestOptions<any, any>> = new Map();
   inFlight: Map<string, unknown> = new Map();
   errorCache: Map<string, { error: any; expiry: number }> = new Map();
   
   loadFinish: Subject<string> = new Subject();
   /**
    * Request params (opts)
    * - url {string} - url the request will be sent to
    * - [params] {object} - required if request is POST
    * - [pipe] {OperatorFunction} - pipe that will be ran on returning data
    * - [refresher] {Subject<void>} - optional refresher to call when data is updated
    * - [length] {number} - length of time in minutes the cache will be stored ( default 5 )
    */
   request<T, R>( opts: RequestOptions<T, R> ): Observable<R> {
      opts = new RequestOptions( opts );

      // A fresh data request clears any previous "stopped" state left behind by
      // pagination.stop() (e.g. triggered on route change). Without this reset the
      // loadingStop flag stays true for the whole session and views gated on
      // !pagination.loadingStop (like the invoices bar chart) never render again.
      this.pagination.loadingStop = false;

      if (this.noCache.get(opts.url)) {
         opts.noCache = true;

         this.noCache.delete(opts.url);
      }

      if ( !opts.length ) { opts.length = DefaultExpiryLength; }

      if ( this.inFlight.has( opts.cache ) ) {
         return this.inFlight.get( opts.cache ) as Observable<R>;
      }

      // Check if this request recently failed -- prevent infinite retries
      const cachedError = this.errorCache.get( opts.cache );
      if ( cachedError && cachedError.expiry > Date.now() ) {
         return throwError( () => cachedError.error );
      } else if ( cachedError ) {
         this.errorCache.delete( opts.cache );
      }

      const cachedReq = this.subscriptions.get( opts.cache );

      if ( cachedReq && cachedReq.expiry > Date.now() ) {
         // Check for incomplete pagination that needs resumption (but not if already paginating)
         if ( cachedReq.pagination?.hash &&
              cachedReq.fetchedPages &&
              cachedReq.totalPages &&
              cachedReq.fetchedPages.size < cachedReq.totalPages &&
              !this.pagination.loading ) {
            // Resume pagination in background - user will see cached partial data immediately
            this._resumePagination( opts, cachedReq );
         }
         return of( cachedReq.collection );
      } else {
         // cache expired
         this.subscriptions.delete( opts.cache );
         this.inFlight.delete( opts.cache );

         this.inFlight.set( opts.cache, this._request( opts ) );
      }

      // returing inFlight request
      return this.inFlight.get( opts.cache ) as Observable<R>;
   }

   private _resumePagination<T, R>( opts: RequestOptions<T, R>, cachedReq: RequestOptions<T, R> ) {
      // Calculate remaining pages
      const allPages = Array.from({ length: cachedReq.totalPages }, ( _, i ) => i + 1 );
      const remainingPages = allPages.filter( p => !cachedReq.fetchedPages.has( p ) );

      if ( remainingPages.length === 0 ) { return; }

      // Clear cancelled flag so pagination can resume
      cachedReq.cancelled = false;
      this.subscriptions.set( opts.cache, cachedReq );

      // Build the first response from cached collection for merging
      const firstResponse = {
         response: {
            collection: cachedReq.collection,
            pagination: cachedReq.pagination
         }
      };

      // Resume background pagination with remaining pages
      // Pass fetchedPages and pagination to preserve progress
      const resumeOpts = new RequestOptions({
         ...opts,
         ...cachedReq,  // Include all cached data including pagination
         fetchedPages: cachedReq.fetchedPages,
         totalPages: cachedReq.totalPages
      });

      this._paginateInBackground( resumeOpts, { useLambda: opts.useLambda, useLocalLambda: opts.useLocalLambda }, firstResponse, remainingPages );
   }

   private _httpRequest<T, R>( opts: RequestOptions<T, R>, params: any, requestBodyOverride?: any ) {
      const body = requestBodyOverride ?? opts.params;

      return (
         body ?
         this.http.post( opts.url, body, { headers: opts.headers, params: params } ) :
         this.http.get( opts.url, { headers: opts.headers, params: params } )
      );
   }

   private _mergePaginatedResponses( firstResponse: any, pageResponses: any[] ) {
      // Merge all paginated `collection` results into a single response payload
      // so downstream pipes/caching treat it like one response.
      const allCollections: any[] = [
         ...( firstResponse?.response?.collection ?? [] ),
         ...pageResponses.reduce(( acc, r ) => acc.concat( r?.response?.collection ?? [] ), [] )
      ];

      return {
         ...firstResponse,
         response: {
            ...firstResponse?.response,
            collection: allCollections,
         },
      };
   }

   private _paginateInBackground<T, R>( opts: RequestOptions<T, R>, params: any, firstResponse: any, resumePages?: number[] ) {
      // We return page 1 to the caller immediately (so screens can render), then
      // fetch remaining pages concurrently in the background and update the cache
      // when everything completes.
      if ( !opts?.pagination || opts.pagination.page !== 1 || opts.pagination.total <= 1 ) { return; }

      // Initialize fetched pages tracking (page 1 is already fetched)
      const fetchedPages = new Set<number>( opts.fetchedPages ?? [1] );
      const totalPages = opts.totalPages ?? opts.pagination.total;

      this.subscriptions.delete( opts.cache );
      this.loadFinish.next('pending');
      this.pagination.hash = opts.pagination.hash;
      this.pagination.loading = true;

      const cancel$ = this.pagination.stop$.pipe(
         filter( hash => hash === opts.pagination.hash ),
         take( 1 )
      );

      // Calculate remaining pages to fetch (excluding already fetched)
      const allPages = resumePages ?? Array.from({ length: totalPages - 1 }, ( _, i ) => i + 2 );
      const pages = allPages.filter( p => !fetchedPages.has( p ) );

      // If no pages to fetch (complete), clean up and return
      if ( pages.length === 0 ) {
         this.loadFinish.next('finished');
         this.pagination.loading = false;
         this.pagination.loadingPercent = 100;
         this.pagination.reset();
         return;
      }

      let completedPages = fetchedPages.size;
      const pageResponses = [];
      // Initialize progress based on already fetched pages.
      this.pagination.loadingPercent = +( completedPages / totalPages * 100 ).toFixed();

      from( pages ).pipe(
         mergeMap(( page: number ) => {
            const requestBody = opts.params ? { ...( opts.params as any ) } : undefined;
            if ( requestBody ) {
               requestBody.pagination = { ...opts.pagination, page };
            }

            return this._httpRequest( opts, params, requestBody ).pipe(
               switchMap( response => {
                  pageResponses.push( response );
                  fetchedPages.add( page );
                  return of( this._mergePaginatedResponses( firstResponse, pageResponses ) ).pipe(
                     opts.pipe,
                     take( 1 ),
                     tap( collection => {
                        const cached = this.subscriptions.get( opts.cache ) ?? opts;
                        cached.pagination = opts.pagination;
                        cached.fetchedPages = fetchedPages;
                        cached.totalPages = totalPages;

                        this.subscriptions.set( opts.cache, ({
                           ...cached,
                           collection,
                           expiry: new Date().getTime() + ( opts.length * 60 * 1000 ),
                        }));

                        if ( opts.refresher ) { opts.refresher.next(); }
                     }),
                     map( () => ({ ok: true as const, page }) ),
                  );
               }),
               catchError( error => of({ ok: false as const, error, page }) ),
               takeUntil( cancel$ ),
               finalize( () => {
                  completedPages++;
                  this.pagination.loadingPercent = +( completedPages / totalPages * 100 ).toFixed();
               })
            );
         }, CacheService.PARALLEL_REQUESTS_LIMIT ),
         takeUntil( cancel$ ),
      ).subscribe({
         complete: () => {
            this.loadFinish.next('finished');
            this.pagination.loading = false;
            this.pagination.loadingPercent = 100;
            this.pagination.reset();
         }
      });
   }

   private _request<T, R>( opts: RequestOptions<T, R> ) {
      let params;

      if (opts.noCache) {
         params = { useLambda: opts.useLambda, useLocalLambda: opts.useLocalLambda, noCache: true };
      } else {
         params = { useLambda: opts.useLambda, useLocalLambda: opts.useLocalLambda };
      }

      if ( opts?.params?.pagination?.hash ) {
         this.loadFinish.next('pending');
         this.pagination.hash = opts.params.pagination.hash;
         this.pagination.loading = true;
         this.pagination.loadingPercent = 0;
      }

      return this._httpRequest( opts, params ).pipe(
         switchMap(( response: any ) => {
            if ( response?.response?.pagination ) {
               opts.pagination = response.response.pagination;
            }

            if ( !opts.pagination || opts.pagination.page !== 1 || opts.pagination.total <= 1 ) {
               return of( response );
            }

            // IMPORTANT: the progress component (`<lib-http-pagination>`) shows only when
            // `HttpPagination.loading === true`, so we must set this before background paging.
            this.loadFinish.next('pending');
            this.pagination.hash = opts.pagination.hash;
            this.pagination.loading = true;
            this.pagination.loadingPercent = +( opts.pagination.page / opts.pagination.total * 100 ).toFixed();

            // Kick off concurrent page fetches without blocking the page-1 observable.
            this._paginateInBackground( opts, params, response );
            return of( response );
         }),
         opts.pipe,
         tap( collection => {
            const cached = this.subscriptions.get( opts.cache ) ?? opts;

            if ( opts.pagination ) {
               cached.pagination = opts.pagination;
               // Initialize fetchedPages tracking for paginated requests
               if ( opts.pagination.page === 1 && !cached.fetchedPages ) {
                  cached.fetchedPages = new Set([1]);
                  cached.totalPages = opts.pagination.total;
               }
            }

            this.subscriptions.set( opts.cache, ({
               ...cached,
               collection,
               expiry: new Date().getTime() + ( opts.length * 60 * 1000 ),
            }));

            // Clear any previous error cache on success
            this.errorCache.delete( opts.cache );
         }),
         catchError( error => {
            // Cache the error to prevent infinite retries from re-triggering callers
            this.errorCache.set( opts.cache, {
               error,
               expiry: Date.now() + ( ErrorCacheDuration * 1000 ),
            });

            this.inFlight.delete( opts.cache );

            return throwError( () => error );
         }),
         finalize( () => this.inFlight.delete( opts.cache ) ),
         take( 1 ),
         tap( _ => {
            // Only fire refresher here when there is no background pagination in progress.
            // For paginated responses (total > 1), _paginateInBackground fires the refresher
            // after all pages complete. Firing it here for page 1 would trigger a duplicate
            // list call mid-pagination and corrupt the progress percentage.
            const isPaginating = opts.pagination && opts.pagination.total > 1;
            if ( opts.refresher && !isPaginating ) { opts.refresher.next(); }
         }),
         shareReplay({ bufferSize: 1, refCount: false }),
      );
   }

   // private _paginate<T, R>( opts: RequestOptions<T, R> ): RequestOptions<T, R> {
   //    if ( !opts?.pagination ) { return; } // need an object
      
   //    this.loadFinish.next('pending'); // mark pending if data is still requesting from backend
   //    if ( opts.pagination.page >= opts.pagination.total ) {  // we've reached the end of the pagination
   //       this.loadFinish.next('finished');
   //       this.cancelPagination( opts?.pagination?.hash );

   //       this.pagination.reset();

   //       return opts;
   //    }

   //    // update the http pagination object
   //    this.pagination.hash = opts.pagination.hash;
   //    this.pagination.loading = true;
   //    this.pagination.loadingPercent = +( opts.pagination.page / opts.pagination.total * 100 ).toFixed();

   //    // next page
   //    opts.pagination.page++;
   //    opts.params.pagination = opts.pagination;

   //    this.inFlight.set( opts.cache, this._request<T, R>( opts ) );
   // }

   cancelPagination( hash: string ) {
      for ( const [ key, value ] of this.subscriptions ) {
         if ( value.pagination?.hash === hash ) {
            // Mark as cancelled but PRESERVE fetchedPages tracking for resumption
            // Don't delete pagination entirely - we need it for resume detection
            value.cancelled = true;
            this.subscriptions.set( key, value );
         }
      }
   }

   stopPagination( hash: string ) {
      for ( const [ key, value ] of this.subscriptions ) {
         if ( value.pagination?.hash === hash ) {
            value.cancelled = true;

            this.subscriptions.set( key, value );
         }
      }
   }

   constructor( private http: HttpClient, public pagination: HttpPagination ) {
      this.pagination.stop$.subscribe( hash => this.stopPagination( hash ));
   }

   observable<T>( property: string ): Observable<T> {
      if ( this.check( property )) {
         return of( this.retrieve( property ));
      }
   }

   check( property: string ): boolean {
      if ( !this.expiry[property] ) { delete( this.cached[property] ); }

      return !!this.expiry[property];
   }

   retrieve( property: string ): any {
      if ( !this.check( property )) { return; }

      return this.cached[property];
   }

   /** length is in minutes */
   store( property: string, data: any, length: number = DefaultExpiryLength ): void {
      this.cached[property] = data;
      if ( this.expiry[property] ) { this.expiry[property].unsubscribe(); }
      this.expiry[property] = timer( length * 60 * 1000 ).subscribe( _ => this.expire( property ));
   }

   clearSet( property: string, refresh?: boolean ) {
      if ( !property ) { return; }

      if (refresh) {
         this.noCache.set( property, true );
      }

      Object.keys( this.expiry )
         .filter( prop => prop.includes( property ))
         .map( prop => this.expire( prop ));

      for ( const key of this.subscriptions.keys() ) {
         if ( key.includes( property )) {
            this.subscriptions.delete( key );
         }
      }

      for ( const key of this.inFlight.keys() ) {
         if ( key.includes( property )) {
            this.inFlight.delete( key );
         }
      }

      for ( const key of this.errorCache.keys() ) {
         if ( key.includes( property )) {
            this.errorCache.delete( key );
         }
      }
   }

   expire( property: string ): void {
      if ( this.expiry[property] ) {
         this.expiry[property].unsubscribe();
         delete( this.expiry[property] );
      }

      this.noCache.delete( property );

      this.inFlight.delete( property );
      this.subscriptions.delete( property );
      this.errorCache.delete( property );
   }

   reset(): void {
      this.cached = {};
      Object.keys( this.expiry ).map( key => this.expiry[key].unsubscribe() );
      this.expiry = {};
      this.subscriptions.clear();
      this.inFlight.clear();
      this.noCache.clear();
      this.errorCache.clear();
   }
}
